import { toRecipePayload } from '@/sections/asset/components/tabs/credentials/RecipeWizard/recipePayload';

import {
  type ApiKeyFormData,
  type BearerTokenFormData,
  type CredentialSubmitPayload,
  CredentialType,
  type DynamicConfigState,
  RefreshIntervalUnit,
  type RequestHeader,
} from './types';

// =============================================================================
// API Parameter Builder Functions
// =============================================================================

export function toSeconds(value: number, unit: RefreshIntervalUnit): number {
  switch (unit) {
    case RefreshIntervalUnit.SECONDS:
      return value;
    case RefreshIntervalUnit.MINUTES:
      return value * 60;
    case RefreshIntervalUnit.HOURS:
      return value * 3600;
    default:
      return value;
  }
}

export function sanitizeHeaders(
  headers: RequestHeader[]
): Record<string, string>[] {
  return headers.map(header => ({
    [header.key]: header.value,
  }));
}

export function buildDynamicTokenConfigPayload(
  config: DynamicConfigState
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    request_url: config.tokenServiceUrl,
    request_method: config.requestMethod,
    refresh_interval_seconds: toSeconds(
      config.refreshInterval,
      config.refreshIntervalUnit
    ),
    extract_path: config.tokenLocation,
  };

  if (config.requestHeaders.length > 0) {
    payload.request_headers = sanitizeHeaders(config.requestHeaders);
  }

  if (config.requestBody) {
    payload.request_body = config.requestBody;
  }

  return payload;
}

export function buildBearerTokenParameters(
  data: BearerTokenFormData
): Record<string, unknown> {
  const parameters: Record<string, unknown> = {
    label: data.label,
    auth_type: 'bearer_token',
  };

  if (data.isDynamic) {
    parameters.dynamic_token_config = buildDynamicTokenConfigPayload(
      data.dynamicConfig
    );
  } else {
    parameters.token = data.token;
  }

  return parameters;
}

export function buildApiKeyParameters(
  data: ApiKeyFormData
): Record<string, unknown> {
  const parameters: Record<string, unknown> = {
    label: data.label,
    auth_type: 'api_key',
    parameter_name: data.parameterName,
    api_key_destination: data.addTo,
  };

  if (data.isDynamic) {
    parameters.dynamic_token_config = buildDynamicTokenConfigPayload(
      data.dynamicConfig
    );
  } else {
    parameters.api_key = data.apiKey;
  }

  return parameters;
}

// =============================================================================
// Broker payload builder
// =============================================================================

/**
 * The broker body produced by {@link buildBrokerPayload}: the per-type
 * `Type`/`Format`/`Parameters` triple WITHOUT the asset-coupled envelope
 * fields (`Category`/`Operation`/`ResourceKey`), which the caller wraps on
 * before POSTing to `/broker`.
 */
export interface BrokerPayloadBody {
  Type: string;
  Format: string[];
  Parameters: Record<string, unknown>;
}

/**
 * RECORDING is the one type whose body needs file contents. File reading is
 * async and stays caller-side (FileReader), so the caller reads the file and
 * passes the resulting `script` string here, keeping this builder pure/sync.
 */
type BuildBrokerPayloadInput =
  | Exclude<CredentialSubmitPayload, { type: CredentialType.RECORDING }>
  | {
      type: CredentialType.RECORDING;
      data: { label: string; script: string };
    };

/**
 * buildBrokerPayload turns a credential form payload into the broker body
 * (`Type`/`Format`/`Parameters`). Pure and synchronous: RECORDING expects a
 * caller-read `script` string (the FileReader read stays caller-side). The
 * caller wraps the result with `Category`/`Operation`/`ResourceKey` before
 * POSTing to `/broker`.
 */
export function buildBrokerPayload(
  payload: BuildBrokerPayloadInput,
  opts: { isWebService: boolean }
): BrokerPayloadBody {
  const { isWebService } = opts;

  switch (payload.type) {
    case CredentialType.BASIC:
      return {
        Type: 'burp-authentication',
        Format: isWebService ? ['api-auth'] : ['token'],
        Parameters: {
          ...(isWebService ? { auth_type: 'basic' } : {}),
          label: payload.data.label || payload.data.username,
          username: payload.data.username,
          password: payload.data.password,
        },
      };

    case CredentialType.RECORDING:
      return {
        Type: 'burp-authentication',
        Format: ['file'],
        Parameters: {
          label: payload.data.label,
          script: payload.data.script,
        },
      };

    case CredentialType.BEARER_TOKEN:
      return {
        Type: 'burp-authentication',
        Format: ['api-auth'],
        Parameters: buildBearerTokenParameters(payload.data),
      };

    case CredentialType.API_KEY:
      return {
        Type: 'burp-authentication',
        Format: ['api-auth'],
        Parameters: buildApiKeyParameters(payload.data),
      };

    case CredentialType.STATIC_TOKEN: {
      const nonEmptyHeaders = payload.data.headers.filter(
        h => h.key && h.value
      );
      const headersObj = Object.fromEntries(
        nonEmptyHeaders.map(h => [h.key, h.value])
      );
      return {
        Type: 'web-auth',
        Format: ['token'],
        Parameters: {
          label:
            payload.data.label || nonEmptyHeaders[0]?.key || 'Static Token',
          method: 'static-token',
          headers: headersObj,
        },
      };
    }

    case CredentialType.RECIPE:
      return {
        Type: 'web-auth',
        Format: ['token'],
        Parameters: toRecipePayload(payload.data),
      };

    case CredentialType.COMPLEX_LOGIN: {
      const inputsObj = Object.fromEntries(
        payload.data.inputs
          .filter(i => i.key.trim() && i.value)
          .map(i => [i.key.trim(), i.value])
      );
      return {
        Type: 'web-auth',
        Format: ['token'],
        Parameters: {
          method: 'discover-login',
          label: payload.data.label,
          login_url: payload.data.loginUrl,
          inputs: inputsObj,
          ...(payload.data.hint.trim()
            ? { hint: payload.data.hint.trim() }
            : {}),
        },
      };
    }
  }
}
